import React, { useState } from "react";
import { Card, Button, Table, Badge, Tabs, Tab } from "react-bootstrap";
import { IconRefresh } from "@tabler/icons-react";
import Canvas from "components/canvas/canvas";
import { useAgentPointsLog } from "./context/AgentPointsLogContext";

const formatNumber = (value) => {
  return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(
    Number(value) || 0
  );
};

function RankingListSection({ rankings, refetch, Pagination }) {
  const [showAgentCanvas, setShowAgentCanvas] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const {
    pointsLog,
    setAgentId,
    Pagination: PointsLogPagination,
  } = useAgentPointsLog();

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowAgentCanvas(true);
    setAgentId(agent.agentId);
  };

  const handleCloseAgentCanvas = () => {
    setShowAgentCanvas(false);
  };

  const agentDetailBody = selectedAgent ? (
    <Tabs defaultActiveKey="details" id="agent-canvas-tabs">
      <Tab eventKey="details" title="Agent Details">
        <div style={{ padding: "16px" }}>
          <div style={{ marginBottom: "24px", textAlign: "center" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: selectedAgent.agentColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "24px",
                fontWeight: "bold",
                color: "white",
              }}
            >
              {selectedAgent.name.charAt(0).toUpperCase()}
            </div>
            <h4 style={{ marginBottom: "8px" }}>{selectedAgent.name}</h4>
          </div>

          <div style={{ display: "grid", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Ranking Position:</strong>
              <span>#{selectedAgent.no}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Points:</strong>
              <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                {selectedAgent.points}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Current Tier:</strong>
              <Badge
                style={{
                  backgroundColor: selectedAgent.tierColor,
                  color:
                    selectedAgent.ranking === "Gold" ||
                    selectedAgent.ranking === "Platinum" ||
                    selectedAgent.ranking === "Diamond"
                      ? "#000000"
                      : "#ffffff",
                }}
              >
                {selectedAgent.ranking}
              </Badge>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Season:</strong>
              <span>{selectedAgent.season}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Agent Color:</strong>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: selectedAgent.agentColor,
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                  }}
                />
                <span style={{ fontFamily: "monospace" }}>
                  {selectedAgent.agentColor}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Tab>
      <Tab eventKey="points-log" title="Points Log">
        <div style={{ padding: "16px" }}>
          <div style={{ overflowX: "auto" }}>
            <Table
              striped
              bordered
              hover
              size="sm"
              style={{ minWidth: "760px", marginBottom: 0 }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th style={{ textAlign: "center" }}>Points</th>
                  <th>Description</th>
                  <th>Season</th>
                </tr>
              </thead>
              <tbody>
                {pointsLog?.loading ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>
                      Loading points log...
                    </td>
                  </tr>
                ) : pointsLog?.error ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", color: "red" }}>
                      Error: {pointsLog.error}
                    </td>
                  </tr>
                ) : pointsLog.data && pointsLog.data.length > 0 ? (
                  pointsLog.data.map((log, index) => (
                    <tr key={log.id || index}>
                      <td>{new Date(log.date).toLocaleDateString()}</td>
                      <td>{log.type?.replace("_", " ")}</td>
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          color: log.points > 0 ? "green" : "red",
                        }}
                      >
                        {log.points > 0 ? "+" : ""}
                        {log.points}
                      </td>
                      <td>{log.description}</td>
                      <td>{log.season?.name || "Unknown"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>
                      No points log available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {pointsLog?.pagination && (
            <div style={{ padding: "16px 0 0" }}>{PointsLogPagination}</div>
          )}
        </div>
      </Tab>
    </Tabs>
  ) : null;

  return (
    <>
      <Card className="p-3">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>Rangking List</h2>
          <Button
            variant="outline-info"
            onClick={refetch}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <IconRefresh size={16} />
            Refresh Data
          </Button>
        </div>
        <div style={{ padding: "0", overflowX: "auto" }}>
          <Table
            striped
            bordered
            hover
            style={{ marginBottom: "0", minWidth: "600px" }}
          >
            <thead>
              <tr>
                <th style={{ width: "64px" }}>📋 No</th>
                <th>📝 Nama</th>
                <th style={{ textAlign: "center" }}>⭐ Points</th>
                <th style={{ textAlign: "center" }}>🎯 Ranking</th>
                <th>📅 Season</th>
              </tr>
            </thead>
            <tbody>
              {rankings?.loading ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    <div className="d-flex justify-content-center align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Loading rankings data...
                    </div>
                  </td>
                </tr>
              ) : rankings?.error ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    <div className="alert alert-danger" role="alert">
                      <strong>Error loading rankings:</strong> {rankings.error}
                    </div>
                  </td>
                </tr>
              ) : rankings.data && rankings.data.length > 0 ? (
                rankings.data.map((ranking, index) => (
                  <tr key={ranking.id || index}>
                    <td style={{ fontWeight: "500" }}>{index + 1}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            backgroundColor: ranking.agent?.color || "#6b7280",
                            border: "1px solid #dee2e6",
                            cursor: "pointer",
                          }}
                          className="p-1 rounded-2"
                          onClick={() =>
                            handleAgentClick({
                              no: index + 1,
                              name: ranking.agent?.name || "Unknown Agent",
                              points: ranking.points || 0,
                              ranking: ranking.tier?.name || "Bronze",
                              season: ranking.season?.name || "Unknown Season",
                              agentColor: ranking.agent?.color || "#6b7280",
                              tierColor: ranking.tier?.color || "#6b7280",
                              agentId: ranking.agent?.id,
                            })
                          }
                        >
                          {ranking.agent?.name || "Unknown Agent"}
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center", fontWeight: "500" }}>
                      {formatNumber(ranking.points)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Badge
                        style={{
                          backgroundColor: ranking.tier?.color || "#6b7280",
                          color:
                            (ranking.tier?.name || "Bronze") === "Gold" ||
                            (ranking.tier?.name || "Bronze") === "Platinum" ||
                            (ranking.tier?.name || "Bronze") === "Diamond"
                              ? "#000000"
                              : "#ffffff",
                        }}
                        bg={ranking.tier?.color || "#6b7280"}
                        text={
                          (ranking.tier?.name || "Bronze") === "Gold"
                            ? "light"
                            : "dark"
                        }
                      >
                        {ranking.tier?.name || "Bronze"}
                      </Badge>
                    </td>
                    <td style={{ fontSize: "12px", color: "#6c757d" }}>
                      {ranking.season?.name || "Unknown Season"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No ranking data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {Pagination && <div style={{ padding: "16px" }}>{Pagination}</div>}
        </div>
      </Card>

      <Canvas
        show={showAgentCanvas}
        handleClose={handleCloseAgentCanvas}
        title="Agent Details"
        body={agentDetailBody}
        className="large-canvas"
      />
    </>
  );
}

export default RankingListSection;
